#
<script>
import V4Button from 'dashboard/components-next/button/Button.vue';
import { useAlert, useTrack } from 'dashboard/composables';
import ReportFilterSelector from './components/FilterSelector.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import moment from 'moment';
import ReportsAPI from 'dashboard/api/reports';
import ReportContainer from './ReportContainer.vue';
import ReportHeader from './components/ReportHeader.vue';
import ConversationReportsTranscriptsDownloadDialog from './ConversationReportsTranscriptsDownloadDialog.vue';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

export default {
  name: 'ConversationReports',
  components: {
    ReportHeader,
    ReportFilterSelector,
    ReportContainer,
    V4Button,
    ConversationReportsTranscriptsDownloadDialog,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
      isTranscriptsBulkDownloading: false,
      isTranscriptsSynthesisLoading: false,
    };
  },
  methods: {
    fetchAllData() {
      this.fetchAccountSummary();
      this.fetchChartData();
    },
    fetchAccountSummary() {
      try {
        this.$store.dispatch('fetchAccountSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      [
        'CONVERSATIONS',
        'INCOMING_MESSAGES',
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'REPLY_TIME',
      ].forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: REPORTS_KEYS[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const { from, to, groupBy, businessHours } = this;

      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
      };
    },
    downloadConversationReports() {
      const { from, to } = this;
      const fileName = generateFileName({
        type: 'conversation',
        to,
        businessHours: this.businessHours,
      });
      this.$store.dispatch('downloadConversationsSummaryReports', {
        from,
        to,
        fileName,
        businessHours: this.businessHours,
      });
    },
    openTranscriptsDownloadDialog() {
      this.$refs.conversationTranscriptsDownloadDialogRef?.open();
    },
    archiveFilenameFromTranscriptsHeaders(headers) {
      const contentDisposition = headers?.['content-disposition'] || '';
      const encodedMatch = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/i
      );
      if (encodedMatch?.[1]) {
        return decodeURIComponent(encodedMatch[1]);
      }

      const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      if (plainMatch?.[1]) {
        return plainMatch[1];
      }

      return `transcricoes-conversas-${moment().format('YYYY-MM-DD_HH-mm-ss')}.tar.gz`;
    },
    async parseTranscriptsErrorBlob(data) {
      if (!(data instanceof Blob)) {
        return null;
      }
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed.error || parsed.message || text;
      } catch {
        return text;
      }
    },
    transcriptsNotFoundError(payload) {
      if (payload.agentContactPair) {
        return this.$t(
          'CONVERSATION_TRANSCRIPTS_DOWNLOAD.ERRORS.NO_RESULTS_FOR_PAIR'
        );
      }

      return this.$t('CONVERSATION_TRANSCRIPTS_DOWNLOAD.ERRORS.NO_RESULTS');
    },
    async onTranscriptsSynthesizeConfirm(payload) {
      if (this.isTranscriptsSynthesisLoading) {
        return;
      }

      this.isTranscriptsSynthesisLoading = true;

      try {
        const response = await ReportsAPI.synthesizeConversationTranscripts({
          from: payload.from,
          to: payload.to,
          agentIds: payload.agentIds,
          contactIds: payload.contactIds,
          useDateRange: payload.useDateRange,
          agentContactPair: payload.agentContactPair,
          questions: payload.questions || [],
        });

        const fileName =
          this.archiveFilenameFromTranscriptsHeaders(response.headers) ||
          `sintese-transcricoes-${moment().format('YYYY-MM-DD_HH-mm-ss')}.txt`;

        const blob = new Blob([response.data], {
          type: response.headers?.['content-type'] || 'text/plain',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
        this.$refs.conversationTranscriptsDownloadDialogRef?.closeSynthesis?.();
      } catch (error) {
        const status = error.response?.status;
        let message = null;
        if (error.response?.data instanceof Blob) {
          message = await this.parseTranscriptsErrorBlob(error.response.data);
        }

        if (status === 503) {
          useAlert(
            this.$t('CONVERSATION_TRANSCRIPTS_SYNTHESIS.ERRORS.NOT_CONFIGURED')
          );
        } else if (status === 502) {
          useAlert(
            message ||
              this.$t('CONVERSATION_TRANSCRIPTS_SYNTHESIS.ERRORS.LLM_ERROR')
          );
        } else if (status === 404) {
          useAlert(message || this.transcriptsNotFoundError(payload));
        } else if (status === 422) {
          useAlert(
            this.$t('CONVERSATION_TRANSCRIPTS_DOWNLOAD.ERRORS.INVALID_OR_LIMIT')
          );
        } else {
          useAlert(
            message ||
              this.$t('CONVERSATION_TRANSCRIPTS_SYNTHESIS.ERRORS.FAILED')
          );
        }
      } finally {
        this.isTranscriptsSynthesisLoading = false;
      }
    },
    async onTranscriptsDownloadConfirm(payload) {
      if (this.isTranscriptsBulkDownloading) {
        return;
      }

      this.isTranscriptsBulkDownloading = true;

      try {
        const response = await ReportsAPI.downloadConversationTranscripts({
          from: payload.from,
          to: payload.to,
          agentIds: payload.agentIds,
          contactIds: payload.contactIds,
          useDateRange: payload.useDateRange,
          agentContactPair: payload.agentContactPair,
        });

        const fileName = this.archiveFilenameFromTranscriptsHeaders(
          response.headers
        );
        const blob = new Blob([response.data], {
          type: response.headers?.['content-type'] || 'application/gzip',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
        this.$refs.conversationTranscriptsDownloadDialogRef?.close();
      } catch (error) {
        if (error.response?.status === 404) {
          useAlert(this.transcriptsNotFoundError(payload));
        } else if (error.response?.status === 422) {
          useAlert(
            this.$t('CONVERSATION_TRANSCRIPTS_DOWNLOAD.ERRORS.INVALID_OR_LIMIT')
          );
        } else {
          useAlert(this.$t('CONVERSATION_TRANSCRIPTS_DOWNLOAD.ERRORS.FAILED'));
        }
      } finally {
        this.isTranscriptsBulkDownloading = false;
      }
    },
    onFilterChange({ from, to, groupBy, businessHours }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: { from, to, groupBy, businessHours },
        reportType: 'conversations',
      });
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('REPORT.HEADER')">
    <div class="flex flex-wrap items-center justify-end gap-2">
      <V4Button
        :label="$t('REPORT.DOWNLOAD_CONVERSATION_TRANSCRIPTS_BULK')"
        icon="i-ph-chat-text"
        size="sm"
        slate
        @click="openTranscriptsDownloadDialog"
      />
      <V4Button
        :label="$t('REPORT.DOWNLOAD_CONVERSATION_REPORTS')"
        icon="i-ph-download-simple"
        size="sm"
        @click="downloadConversationReports"
      />
    </div>
  </ReportHeader>
  <div class="flex flex-col gap-3">
    <ReportFilterSelector
      :show-agents-filter="false"
      show-group-by-filter
      @filter-change="onFilterChange"
    />
    <ReportContainer :group-by="groupBy" />
  </div>
  <ConversationReportsTranscriptsDownloadDialog
    ref="conversationTranscriptsDownloadDialogRef"
    :initial-from="from"
    :initial-to="to"
    :is-downloading="isTranscriptsBulkDownloading"
    :is-synthesizing="isTranscriptsSynthesisLoading"
    @download="onTranscriptsDownloadConfirm"
    @synthesize="onTranscriptsSynthesizeConfirm"
  />
</template>
